import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, Button, Press, ProgressBar } from "../ui";
import { Icon } from "../Icon";
import { ProgressRing } from "../ProgressRing";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { useAppState } from "../state";
import { LESSONS, t } from "../content";

function ExamDateModal({ visible, onClose }) {
  const { tr, lang } = useI18n();
  const { setExamDate } = useAppState();
  if (!visible) return null;
  const set = (days) => { setExamDate(new Date(Date.now() + days * 86400000).toISOString()); onClose(); };
  const presets = [7, 14, 21, 30, 45, 60];
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", justifyContent: "center" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, backgroundColor: "rgba(15,37,64,0.4)" }} />
      <View style={{
        position: "absolute", bottom: 0, width: "100%", maxWidth: 480, backgroundColor: colors.surface,
        borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24,
      }}>
        <Text style={{ fontWeight: fonts.bold, fontSize: 20, color: colors.onSurface, marginBottom: 4 }}>{tr("examDateTitle")}</Text>
        <Text style={{ fontWeight: fonts.regular, fontSize: 14, color: colors.muted, marginBottom: 20 }}>{tr("examDateHint")}</Text>
        <View row style={{ flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
          {presets.map((d) => (
            <Press key={d} testID={`exam-preset-${d}`} onClick={() => set(d)} style={{
              width: "30%", padding: "16px 0", borderRadius: 14, backgroundColor: colors.surfaceSecondary,
              border: `1px solid ${colors.border}`, display: "flex", flexDirection: "column", alignItems: "center",
            }}>
              <Text style={{ fontWeight: fonts.bold, fontSize: 20, color: colors.brandPrimary }}>{d}</Text>
              <Text style={{ fontWeight: fonts.medium, fontSize: 12, color: colors.muted }}>{lang === "de" ? "Tage" : "days"}</Text>
            </Press>
          ))}
        </View>
        <Button testID="exam-clear" title={lang === "de" ? "Datum entfernen" : "Clear date"} variant="secondary" onPress={() => { setExamDate(null); onClose(); }} />
      </View>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const { user } = useAuth();
  const st = useAppState();
  const [examModal, setExamModal] = useState(false);

  useEffect(() => { st.touchStreak(); }, []); // eslint-disable-line

  const first = (st.state.profile.name || user?.name || "Melly").split(" ")[0];
  const days = st.daysToExam();
  const readiness = st.readiness();
  const due = st.dueFlashcardIds().length;
  const nextLesson = LESSONS.find((l) => !st.state.lessons[l.id]?.completed);

  const tasks = [
    st.state.mistakes.length > 0
      ? { icon: "warning", color: colors.warning, title: `${tr("practiceMistakes")} (${st.state.mistakes.length})`, min: 4, go: () => navigate("/session?mode=mistakes") }
      : nextLesson
      ? { icon: "book-open", color: colors.brandPrimary, title: t(nextLesson.title, lang), min: nextLesson.minutes, go: () => navigate(`/lesson/${nextLesson.id}`) }
      : { icon: "book-open", color: colors.brandPrimary, title: tr("reviewMotivation"), min: 4, go: () => navigate("/session?mode=quick") },
    due > 0
      ? { icon: "cards", color: colors.info, title: `${tr("flashcards")} (${due})`, min: 5, go: () => navigate("/flashcards") }
      : { icon: "scales", color: colors.info, title: tr("solveCase"), min: 6, go: () => navigate("/session?mode=cases") },
    { icon: "cap", color: colors.success, title: `5 ${tr("examQuestions")}`, min: 4, go: () => navigate("/session?mode=quick") },
  ];
  const totalMin = tasks.reduce((s, x) => s + x.min, 0);

  return (
    <View style={{ flex: 1, backgroundColor: colors.surfaceSecondary }}>
      <View style={{ padding: "24px 20px 32px" }}>
        <View row style={{ alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontWeight: fonts.regular, fontSize: 16, color: colors.muted }}>{tr("goodMorning")},</Text>
            <Text style={{ fontWeight: fonts.bold, fontSize: 30, color: colors.onSurface }}>{first} 👋</Text>
          </View>
          <Press testID="home-bell" onClick={() => navigate("/progress")} style={{
            width: 44, height: 44, borderRadius: 22, backgroundColor: colors.surface, display: "flex",
            alignItems: "center", justifyContent: "center", border: `1px solid ${colors.border}`, flexShrink: 0,
          }}>
            <Icon name="flame" size={22} color={colors.warning} weight="fill" />
          </Press>
        </View>

        <Press testID="exam-countdown" onClick={() => setExamModal(true)}>
          <Card style={{ flexDirection: "row", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <View style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: colors.brandTertiary, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="calendar" size={26} color={colors.brandPrimary} weight="fill" />
            </View>
            <View style={{ flex: 1 }}>
              {days !== null ? (
                <>
                  <Text style={{ fontWeight: fonts.bold, fontSize: 22, color: colors.onSurface }}>{days} {lang === "de" ? "Tage" : "days"}</Text>
                  <Text style={{ fontWeight: fonts.regular, fontSize: 13, color: colors.muted }}>{tr("daysToExam")}</Text>
                </>
              ) : (
                <>
                  <Text style={{ fontWeight: fonts.semibold, fontSize: 16, color: colors.onSurface }}>{tr("setExamDate")}</Text>
                  <Text style={{ fontWeight: fonts.regular, fontSize: 13, color: colors.muted }}>{tr("examDateHint")}</Text>
                </>
              )}
            </View>
            <Icon name="caret-right" size={20} color={colors.muted} />
          </Card>
        </Press>

        <View row style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Text style={{ fontWeight: fonts.bold, fontSize: 18, color: colors.onSurface }}>{tr("todayForYou")}</Text>
          <Text style={{ fontWeight: fonts.medium, fontSize: 13, color: colors.brandPrimary }}>{totalMin} {lang === "de" ? "Minuten" : "minutes"}</Text>
        </View>

        <Card style={{ padding: 8, marginBottom: 20 }}>
          {tasks.map((task, i) => (
            <Press key={i} testID={`today-task-${i}`} onClick={task.go} style={{
              display: "flex", flexDirection: "row", alignItems: "center", gap: 14, padding: 12,
              borderBottom: i < tasks.length - 1 ? `1px solid ${colors.divider}` : "none",
            }}>
              <View style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: colors.surfaceSecondary, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={task.icon} size={20} color={task.color} weight="fill" />
              </View>
              <Text numberOfLines={1} style={{ flex: 1, fontWeight: fonts.semibold, fontSize: 15, color: colors.onSurface }}>{task.title}</Text>
              <Text style={{ fontWeight: fonts.medium, fontSize: 13, color: colors.muted, flexShrink: 0 }}>{task.min} {tr("min")}</Text>
            </Press>
          ))}
        </Card>

        <Text style={{ fontWeight: fonts.bold, fontSize: 18, color: colors.onSurface, marginBottom: 12 }}>{tr("yourProgress")}</Text>
        <Press testID="home-progress" onClick={() => navigate("/progress")}>
          <Card style={{ flexDirection: "row", alignItems: "center", gap: 20, marginBottom: 24 }}>
            <ProgressRing progress={readiness} size={96} stroke={10} label={`${readiness}%`} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontWeight: fonts.semibold, fontSize: 15, color: colors.onSurface, marginBottom: 4 }}>{tr("examReadiness")}</Text>
              <Text style={{ fontWeight: fonts.regular, fontSize: 13, color: colors.muted, marginBottom: 10 }}>
                {readiness >= 75 ? tr("strong") : readiness >= 50 ? tr("improve") : tr("critical")}
              </Text>
              <ProgressBar progress={readiness} color={readiness >= 75 ? colors.success : readiness >= 50 ? colors.warning : colors.error} />
            </View>
          </Card>
        </Press>

        <Button testID="start-training-btn" title={tr("startTraining")} icon="lightning" onPress={() => navigate("/session?mode=daily")} />
      </View>
      <ExamDateModal visible={examModal} onClose={() => setExamModal(false)} />
    </View>
  );
}
